import './chat-message-mind-feedback-form.scss';

import { useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../data/context/app-context';
import {
  IgnoreMindFeedbackDocument,
  MindFeedbackType,
  RateMindFeedbackDocument,
  SaveMindFeedbackDocument,
  TransformMindAdviceDocument,
} from '../../../data/types/api/graphql';
import { AlertMessageType } from '../../alert-message/alert-message';
import { Button, ButtonIcon, ButtonType } from '../../button/button';
import { FilterButton } from '../../filter-button/filter-button';
import { InputTextAreaController } from '../../form/controllers/input-textarea';
import { CustomFormattedMessage } from '../../intl/custom-formatted-message';
import { ToggleIconButton } from '../../toggle-icon-button/toggle-icon-button';
import { useChatMessageMindFeedbackForm } from './form/use-chat-message-mind-feedback-form';

type FilterMode = 'original' | 'simplified' | 'ai-friendly' | 'low-literacy';

const GP_PRACTICE_SUFFIX =
  ' Write the text as if it comes from a person at the GP practice.' +
  ' Leave out any short summary or repetition of the complaint.' +
  ' Make the text directly readable as advice from the GP practice.' +
  ' For any section about change of symptoms, the advice should be to contact us again via this chat.';

const FILTER_PROMPTS: Record<Exclude<FilterMode, 'original'>, string> = {
  simplified: `Simplify this advice to be easy to understand and make it shorter.${
    GP_PRACTICE_SUFFIX
  }`,
  'ai-friendly': `Rewrite this advice in a clear, structured and professional way suitable for a digital health platform.${
    GP_PRACTICE_SUFFIX
  }`,
  'low-literacy': `Rewrite this advice as short as possible. Use only simple words and very short sentences. B1 level. No medical jargon.${
    GP_PRACTICE_SUFFIX
  }`,
};

type ChatMessageMindFeedbackProps = {
  messageId: string;
  text?: string;
  feedback?: MindFeedbackType;
  readonly?: boolean;
};

export function ChatMessageMindFeedbackForm({
  messageId,
  text,
  readonly,
  feedback,
}: ChatMessageMindFeedbackProps) {
  const [rateMindFeedback] = useMutation(RateMindFeedbackDocument);
  const [saveMindFeedback, { loading: saveMindFeedbackLoading }] = useMutation(
    SaveMindFeedbackDocument
  );
  const [ignoreMindFeedback] = useMutation(IgnoreMindFeedbackDocument);
  const intl = useIntl();
  const { showAlertMessage } = useAppContext();
  const { form, formIsValid } = useChatMessageMindFeedbackForm();
  const [selectedFeedback, setSelectedFeedback] = useState<MindFeedbackType>(
    feedback ?? MindFeedbackType.Neutral
  );
  const [showReadonly, setShowReadonly] = useState(!readonly);
  const [filterMode, setFilterMode] = useState<FilterMode>('original');
  const [aiTextCache, setAiTextCache] = useState<
    Partial<Record<FilterMode, string>>
  >({});
  const [loadingMode, setLoadingMode] = useState<FilterMode | null>(null);
  const [transformMindAdvice] = useLazyQuery(TransformMindAdviceDocument);

  useEffect(() => {
    if (text) {
      form.reset({ text });
      setFilterMode('original');
      setAiTextCache({});
      setLoadingMode(null);
    }
  }, [form, text]);

  const isThumbsUpActive = selectedFeedback === MindFeedbackType.Positive;
  const isThumbsDownActive = selectedFeedback === MindFeedbackType.Negative;

  const handleThumbsUp = useCallback(() => {
    const newFeedback =
      selectedFeedback === MindFeedbackType.Positive
        ? MindFeedbackType.Neutral
        : MindFeedbackType.Positive;
    form.setValue('feedback', newFeedback);
    setSelectedFeedback(newFeedback);
    rateMindFeedback({
      variables: {
        messageId,
        feedback: newFeedback,
      },
    })
      .then(result => {
        if (result.data?.mindFeedbackCreate.boolean) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'common.feedback-rated',
            }),
          });
        }
      })
      .catch(error => {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      });
  }, [
    selectedFeedback,
    form,
    messageId,
    rateMindFeedback,
    showAlertMessage,
    intl,
  ]);

  const handleThumbsDown = useCallback(() => {
    const newFeedback =
      selectedFeedback === MindFeedbackType.Negative
        ? MindFeedbackType.Neutral
        : MindFeedbackType.Negative;
    form.setValue('feedback', newFeedback);
    setSelectedFeedback(newFeedback);
    rateMindFeedback({
      variables: {
        messageId,
        feedback: newFeedback,
      },
    })
      .then(result => {
        if (result.data?.mindFeedbackCreate.boolean) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'common.feedback-rated',
            }),
          });
        }
      })
      .catch(error => {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      });
  }, [
    selectedFeedback,
    form,
    messageId,
    rateMindFeedback,
    showAlertMessage,
    intl,
  ]);

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await saveMindFeedback({
          variables: {
            messageId,
            text: values.text,
          },
        });
        if (result.data?.mindFeedbackCreate.boolean) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'common.feedback-sent',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [form, messageId, saveMindFeedback, showAlertMessage, intl]);

  const handleFilterOriginal = useCallback(() => {
    setFilterMode('original');
    form.setValue('text', text ?? '');
  }, [form, text]);

  const handleFilterSelect = useCallback(
    async (mode: Exclude<FilterMode, 'original'>, customPrompt: string) => {
      setFilterMode(mode);
      const cached = aiTextCache[mode];
      if (cached) {
        form.setValue('text', cached);
        return;
      }
      setLoadingMode(mode);
      try {
        const result = await transformMindAdvice({
          variables: { messageId, customPrompt },
        });
        const transformed = result.data?.mindAdviceTransform ?? '';
        setAiTextCache(prev => ({ ...prev, [mode]: transformed }));
        form.setValue('text', transformed);
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      } finally {
        setLoadingMode(null);
      }
    },
    [form, messageId, aiTextCache, transformMindAdvice, showAlertMessage]
  );

  const onHandleIgnore = useCallback(async () => {
    try {
      const result = await ignoreMindFeedback({
        variables: {
          messageId,
        },
      });
      if (result.data?.mindFeedbackCreate.boolean) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'common.feedback-ignored',
          }),
        });
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [messageId, ignoreMindFeedback, showAlertMessage, intl]);

  return (
    <div className="chat-message-mind-feedback-form">
      {readonly ? (
        <>
          <button
            type="button"
            className={`chat-message-mind-feedback-form__header ${
              showReadonly ? 'open' : ''
            }`}
            onClick={() => setShowReadonly(!showReadonly)}
          >
            <i className="icon chevron" />
            <CustomFormattedMessage
              id={`common.advice.${showReadonly ? 'hide' : 'show'}`}
            />
          </button>
          {showReadonly && (
            <div className="chat-message-mind-feedback-form__readonly">
              {text}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="chat-message-mind-feedback-form__content">
            <FormProvider {...form}>
              <InputTextAreaController
                name="text"
                placeholder={intl.formatMessage({
                  id: 'agenda.appointment-create.note-patient-placeholder',
                })}
                disabled={loadingMode !== null}
              />
            </FormProvider>
          </div>
          <div className="chat-message-mind-feedback-form__ai">
            <FilterButton
              active={filterMode === 'original'}
              onClick={handleFilterOriginal}
            >
              <CustomFormattedMessage id="common.filter.original-advice" />
            </FilterButton>
            <FilterButton
              active={filterMode === 'simplified'}
              loading={loadingMode === 'simplified'}
              disabled={loadingMode !== null && loadingMode !== 'simplified'}
              onClick={() =>
                handleFilterSelect('simplified', FILTER_PROMPTS.simplified)
              }
            >
              <CustomFormattedMessage id="common.filter.simplified" />
            </FilterButton>
            <FilterButton
              active={filterMode === 'ai-friendly'}
              loading={loadingMode === 'ai-friendly'}
              disabled={loadingMode !== null && loadingMode !== 'ai-friendly'}
              onClick={() =>
                handleFilterSelect('ai-friendly', FILTER_PROMPTS['ai-friendly'])
              }
            >
              <CustomFormattedMessage id="common.filter.ai-friendly" />
            </FilterButton>
            <FilterButton
              active={filterMode === 'low-literacy'}
              loading={loadingMode === 'low-literacy'}
              disabled={loadingMode !== null && loadingMode !== 'low-literacy'}
              onClick={() =>
                handleFilterSelect(
                  'low-literacy',
                  FILTER_PROMPTS['low-literacy']
                )
              }
            >
              <CustomFormattedMessage id="common.filter.low-literacy" />
            </FilterButton>
          </div>
          <div className="chat-message-mind-feedback-form__footer">
            <Button
              type={ButtonType.SECONDARY}
              icon={
                saveMindFeedbackLoading ? ButtonIcon.LOADING : ButtonIcon.CLOSE
              }
              disabled={!messageId || saveMindFeedbackLoading}
              onClick={onHandleIgnore}
            >
              <CustomFormattedMessage id="common.ignore-advice" />
            </Button>

            <Button
              useAsSubmit
              icon={
                saveMindFeedbackLoading
                  ? ButtonIcon.LOADING
                  : ButtonIcon.PAPERPLANE
              }
              disabled={!messageId || !formIsValid || saveMindFeedbackLoading}
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="common.send-message" />
            </Button>
          </div>
        </>
      )}
      {readonly && (
        <div className="chat-message-mind-feedback-form__rating">
          <div className="chat-message-mind-feedback-form__rating__content">
            <div className="chat-message-mind-feedback-form__rating__label">
              <CustomFormattedMessage id="mind-feedback.rating" />
            </div>
          </div>
          <div className="chat-message-mind-feedback-form__rating__buttons">
            <ToggleIconButton
              icon={ButtonIcon.THUMBS_DOWN}
              active={isThumbsDownActive}
              onClick={handleThumbsDown}
              variant="negative"
            />
            <ToggleIconButton
              icon={ButtonIcon.THUMBS_UP}
              active={isThumbsUpActive}
              onClick={handleThumbsUp}
              variant="positive"
            />
          </div>
        </div>
      )}
    </div>
  );
}
